<script setup lang="ts">
import CheckImgUrl from '@/assets/components/loyaltyGameBox/check.webp?url'
import Dots from '@/assets/components/loyaltyGameBox/dots.svg'
import GiftUrl from '@/assets/components/loyaltyGameBox/gift.webp?url'
import BronzeLeagueUrl from '@/assets/components/loyaltyGameBox/League_bronze.webp?url'
import GoldLeagueUrl from '@/assets/components/loyaltyGameBox/League_gold.webp?url'
import PlatinumLeagueUrl from '@/assets/components/loyaltyGameBox/League_platinum.webp?url'
import SapphireLeagueUrl from '@/assets/components/loyaltyGameBox/League_sapphire.webp?url'
import SilverLeagueUrl from '@/assets/components/loyaltyGameBox/League_silver.webp?url'
import PointerImgUrl from '@/assets/components/loyaltyGameBox/pointer.webp?url'
import RibbonUrl from '@/assets/components/loyaltyGameBox/ribbon.webp?url'
import SepUrl from '@/assets/components/loyaltyGameBox/sep.webp?url'
import { openConnectionDialog } from '@/components/shared/GoatConnectGameDialog/GoatConnectGameDialog'
import { tools } from '@/compositions/tools.ts'
import AlertSvg from '/src/assets/loyalty/alert.svg'
import InfinitySvg from '/src/assets/loyalty/infinity.svg'

const props = defineProps<Props>()
const { routeGame, routeGameKey, isLogged, isLoggedGoat, showLogInDialog } = gamesUtils
const gameLoyalty = computed<Games.Lax<Games.Loyalty>>(() => {
  return useGameStore().loyaltyFor(routeGameKey.value) || null
})

interface Props {
  loyaltyLeagues?: { [key in Games.GameKeyType]?: Games.LoyaltyLeagues[] }
  loyaltyLeagueRewards?: { [key in Games.GameKeyType]?: Record<string, Games.LoyaltyLeaguesRewards[]> }
  loyaltyLeaguesPlayer: { [key in Games.GameKeyType]?: Games.LoyaltyLeaguesPlayer }
}
const { toHumanNumber } = tools

const activeTab = ref('')
const leagues = ref<{ [key in Games.GameKeyType]?: Games.LoyaltyLeagues[] }>({})
const activeLeague = ref<{ [key in Games.GameKeyType]?: Games.LoyaltyLeagues }>({})

function getEmptyLeague(): Games.LoyaltyLeagues[] {
  return [
    {
      index: 0,
      assetBar: '',
      assetLogo: BronzeLeagueUrl,
      id: 1,
      name: 'Bronze',
      label: '',
      no: 1,
      rewardsCount: 0,
      active: false,
      rewards: [],
      receivedRewards: 0,
      progress: '0%',
      translateProgressTitle: false,
      progressLabel: ''
    },
    {
      index: 1,
      assetBar: '',
      assetLogo: SilverLeagueUrl,
      id: 2,
      name: 'Silver',
      label: '',
      no: 2,
      rewardsCount: 0,
      active: false,
      rewards: [],
      receivedRewards: 0,
      progress: '0%',
      translateProgressTitle: false,
      progressLabel: ''
    },
    {
      index: 2,
      assetBar: '',
      assetLogo: GoldLeagueUrl,
      id: 3,
      name: 'Gold',
      label: '',
      no: 3,
      rewardsCount: 0,
      active: false,
      rewards: [],
      receivedRewards: 0,
      progress: '0%',
      translateProgressTitle: false,
      progressLabel: ''
    },
    {
      index: 3,
      assetBar: '',
      assetLogo: PlatinumLeagueUrl,
      id: 4,
      name: 'Platinum',
      label: '',
      no: 4,
      rewardsCount: 0,
      active: false,
      rewards: [],
      receivedRewards: 0,
      progress: '0%',
      translateProgressTitle: false,
      progressLabel: ''
    },
    {
      index: 4,
      assetBar: '',
      assetLogo: SapphireLeagueUrl,
      id: 5,
      name: 'Sapphire',
      label: '',
      no: 5,
      rewardsCount: 0,
      active: false,
      rewards: [],
      receivedRewards: 0,
      progress: '0%',
      translateProgressTitle: false,
      progressLabel: ''
    }
  ]
}

const currentLeagues = computed(() => {
  return leagues.value?.[routeGameKey.value || '']?.slice(0, 5) || getEmptyLeague()
})

const allLeagues = computed(() => {
  return leagues.value?.[routeGameKey.value || ''] || getEmptyLeague()
})

function renderPropsData() {
  if (!routeGameKey.value) {
    return
  }
  const rewardsMap: Record<string, Games.LoyaltyLeaguesRewards[]> = {}
  const apiRewards = props.loyaltyLeagueRewards?.[routeGameKey.value]
  if (apiRewards) {
    Object.entries(apiRewards).forEach(([leagueKey, leagueRewards]) => {
      if (!rewardsMap[leagueKey]) {
        rewardsMap[leagueKey] = []
      }

      leagueRewards.forEach((reward) => {
        const renderedReward: Games.LoyaltyLeaguesRewards = {
          ...{
            id: 0,
            items: [],
            league: '',
            no: 0,
            req_value: 0,
            taken: 0,
            progress: 0
          },
          ...reward
        }
        if (renderedReward.items?.length) {
          renderedReward.items[0] = { ...{
            amount: 0,
            humanAmount: '0',
            bonusAmount: 0,
            description: '',
            title: '',
            asset: ''
          }, ...renderedReward.items[0] }
          if (renderedReward.items[0]?.asset && routeGame.value?.imgStorageUrl && renderedReward.items[0].asset.substring(0, 4) !== 'http') {
            renderedReward.items[0].asset = routeGame.value.imgStorageUrl + renderedReward.items[0].asset
          }
          if (renderedReward.items[0]?.amount) {
            renderedReward.items[0].humanAmount = toHumanNumber(renderedReward.items[0].amount)
          }
        }
        rewardsMap[leagueKey]?.push(renderedReward)
      })
      rewardsMap[leagueKey].sort((a, b) => a.no - b.no)
    })
  }

  const renderedLeagues = (props.loyaltyLeagues?.[routeGameKey.value] || []).map((league) => {
    const renderedLeague: Games.LoyaltyLeagues = { ...{
      assetBar: '',
      assetLogo: '',
      id: 0,
      name: '',
      label: '',
      no: 0,
      rewardsCount: 0,
      active: props.loyaltyLeaguesPlayer[routeGameKey.value]?.league === league.name,
      rewards: [],
      index: 0,
      progress: '',
      translateProgressTitle: false,
      progressLabel: '',
      receivedRewards: props.loyaltyLeaguesPlayer[routeGameKey.value]?.league === league.name ? props.loyaltyLeaguesPlayer[routeGameKey.value]?.collectedRewards || 0 : 0
    }, ...league }
    if (routeGame.value?.imgStorageUrl) {
      if (renderedLeague.assetBar && renderedLeague.assetBar.substring(0, 4) !== 'http') {
        renderedLeague.assetBar = routeGame.value.imgStorageUrl + renderedLeague.assetBar
      }
      if (renderedLeague.assetLogo && renderedLeague.assetLogo.substring(0, 4) !== 'http') {
        renderedLeague.assetLogo = routeGame.value.imgStorageUrl + renderedLeague.assetLogo
      }
    }
    renderedLeague.rewards = rewardsMap[renderedLeague?.name] ?? []
    // progress logic
    if (renderedLeague?.rewards?.length) {
      const progressRewardsLogic = {
        isActive: renderedLeague.active,
        visibleNumber: (renderedLeague.rewardsCount - renderedLeague.receivedRewards > 3 || renderedLeague.rewardsCount === -1) ? 4 : 3,
        claimed: 0,
        chunkPerc: 0,
        activeIndex: -1,
        leaguePoints: props.loyaltyLeaguesPlayer?.[routeGameKey.value]?.leaguePoints || 0,
        claimedPerc: 0,
        activePerc: 0,
        activeSize: 0,
        activeLabel: '',
        totalPerc: 0
      }
      if (renderedLeague.rewards.length > progressRewardsLogic.visibleNumber) {
        const visibleRewards = rewardsMap[renderedLeague?.name]?.slice(progressRewardsLogic.visibleNumber * -1)
        if (visibleRewards) {
          renderedLeague.rewards = rewardsMap[renderedLeague?.name] = visibleRewards
        }
      }
      progressRewardsLogic.chunkPerc = 100 / progressRewardsLogic.visibleNumber
      for (let n = 0; n < progressRewardsLogic.visibleNumber; n++) {
        if (progressRewardsLogic.isActive) {
          const reward = renderedLeague.rewards[n]
          if (reward) {
            if (reward.req_value <= progressRewardsLogic.leaguePoints) {
              if (reward.taken === 0) {
                reward.taken = 1
              }
              progressRewardsLogic.claimed++
            } else if (progressRewardsLogic.activeIndex === -1) {
              progressRewardsLogic.activeIndex = n
              progressRewardsLogic.activeSize = reward.req_value - (renderedLeague.rewards?.[n - 1]?.req_value || 0)
              progressRewardsLogic.activeLabel = `${progressRewardsLogic.leaguePoints - (renderedLeague.rewards?.[n - 1]?.req_value || 0)}/${progressRewardsLogic.activeSize}`
              if (progressRewardsLogic.activeSize) {
                progressRewardsLogic.activePerc = (progressRewardsLogic.leaguePoints - (renderedLeague.rewards?.[n - 1]?.req_value || 0)) / progressRewardsLogic.activeSize
              }
            }
          }
        } else {
          progressRewardsLogic.claimed++
        }
      }
      renderedLeague.debug = progressRewardsLogic
      progressRewardsLogic.totalPerc = (progressRewardsLogic.claimed * progressRewardsLogic.chunkPerc - (progressRewardsLogic.claimed ? progressRewardsLogic.chunkPerc * 0.5 : 0)) + (progressRewardsLogic.activePerc * progressRewardsLogic.chunkPerc * (progressRewardsLogic.claimed ? 1 : 0.5))
      renderedLeague.progress = `${progressRewardsLogic.totalPerc}%`
      renderedLeague.translateProgressTitle = progressRewardsLogic.totalPerc < 40
      renderedLeague.progressLabel = progressRewardsLogic.activeLabel
    }
    if (renderedLeague.active) {
      activeLeague.value[routeGameKey.value] = renderedLeague
    }
    return renderedLeague
  }).sort((a, b) => (a.no ?? 0) - (b?.no ?? 0))
  if (!renderedLeagues.length) {
    leagues.value[routeGameKey.value] = getEmptyLeague()
    return
  }

  leagues.value[routeGameKey.value] = renderedLeagues
}

const visibleLeagues = computed<Games.LoyaltyLeagues[]>(() => {
  if (currentLeagues.value.length) {
    return currentLeagues.value.slice(0, 5)
  }
  return []
})
const leaguesMap = computed<Record<string, Games.LoyaltyLeagues>>(() => {
  if (currentLeagues.value.length) {
    return Object.fromEntries(currentLeagues.value.map((element, index) => {
      return [element.name, { ...element, index }]
    }))
  }
  return {}
})

function changeTab(name: string) {
  if (isLogged.value) {
    activeTab.value = name
  }
}
const apiActiveTabs = ref<{ [key in Games.GameKeyType]?: string }>({})
function checkActiveTab() {
  // check api data
  if (isLogged.value) {
    (props.loyaltyLeagues?.[routeGameKey.value] || []).some((leaguesData) => {
      if (leaguesData?.name && props.loyaltyLeaguesPlayer?.[routeGameKey.value]?.league === leaguesData.name) {
        if (!apiActiveTabs.value[routeGameKey.value]) {
          activeTab.value = leaguesData.name || ''
        }
        apiActiveTabs.value[routeGameKey.value] = leaguesData.name || ''
        return true
      }
      return false
    })
  }
  if (!activeTab.value) {
    activeTab.value = apiActiveTabs.value[routeGameKey.value] || currentLeagues.value?.[0]?.name || 'Bronze'
  }
  // when api don't return an active flag
  if (!apiActiveTabs.value?.[routeGameKey.value] && currentLeagues.value?.[0] && !currentLeagues.value?.[0].active) {
    currentLeagues.value[0].active = true
  }
}
watch(() => `${routeGameKey.value}_${isLogged.value}`, () => {
  activeTab.value = ''
  if (!routeGameKey.value) {
    return
  }
  if (!leagues.value[routeGameKey.value]) {
    leagues.value[routeGameKey.value] = getEmptyLeague()
  } else if (!isLogged.value) {
    activeTab.value = ''
    leagues.value = {}
    leagues.value[routeGameKey.value] = getEmptyLeague()
    activeLeague.value = {}
    apiActiveTabs.value = {}
  }
  checkActiveTab()
})

// DEBUG set depp on true to debug in vue console
watch(() => props.loyaltyLeagues?.[routeGameKey.value || ''], () => {
  renderPropsData()
  checkActiveTab()
}, { immediate: true, deep: false })

const activeLeagueData = computed<Games.LoyaltyLeagues | null>(() => {
  const activeLeagueName = apiActiveTabs.value[routeGameKey.value] || currentLeagues.value?.[0]?.name || 'Bronze'
  if (leaguesMap.value?.[activeLeagueName]?.active) {
    return leaguesMap.value[activeLeagueName]
  }
  return null
})

const currentActiveLeagueSelected = computed<Games.LoyaltyLeagues | null>(() => {
  if (activeLeagueData.value && activeTab.value && activeTab.value === activeLeagueData.value?.name) {
    return activeLeagueData.value
  }
  return null
})

const currentTabLeague = computed<Games.LoyaltyLeagues | null>(() => {
  if (activeTab.value && activeTab.value === leaguesMap.value?.[activeTab.value]?.name) {
    return leaguesMap.value[activeTab.value] || null
  }
  return null
})

const { t, te } = useI18n()
function tLeague(name: string): string {
  const key = `loyalty.leagues.${name.toLowerCase()}`
  return te(key) ? t(key) : name
}

const { gaSections, gaConnectBtnClick } = gaUtils
const route = useRoute()
function connectClick() {
  gaConnectBtnClick({ gs_game: routeGameKey.value, gs_route: String(route.name), gs_section: gaSections.loyaltyGameBox })
  openConnectionDialog(routeGameKey.value)
}
function loginClick() {
  showLogInDialog(true, { gs_game: routeGameKey.value, gs_route: String(route.name), gs_section: gaSections.loyaltyGameBox })
}
</script>

<template>
  <div
    v-if="routeGame"
    class="relative  w-full m-auto  bg-[#1b5862] min-h-80 gs-disable-placeholder xl:pb-[4vh]"
  >
    <template v-if="currentLeagues?.length">
      <!--  energy and games bar -->
      <div class="bg-[#041a1f]">
        <div class="max-w-lg mx-auto grid grid-cols-[20%_40%_40%] text-white items-center pt-5 big:pt-8">
          <div class="flex items-center justify-center">
            <el-image
              v-if="routeGame?.icon"
              :src="routeGame.icon"
              :alt="`logo ${routeGame.name}`"
              class="w-[2.1rem] sm:w-9 xl:w-10 rounded-lg"
            />
          </div>
          <div
            v-if="routeGame?.energy?.url"
            class="pl-1 pr-6"
          >
            <div
              class="rounded-lg flex justify-start items-center shrink-0 relative pl-5 pr-2 bg-linear-to-b from-[#1d575f] to-[#32788a]"
            >
              <img
                alt="energy"
                :src="routeGame?.energy.url"
                class="absolute"
                :class="[routeGame?.energy?.customClass]"
              >
              <div class="truncate text-white py-0.5 text-lg font-normal tracking-wide">
                {{ gameLoyalty?.energyHuman || 0 }}
              </div>
            </div>
          </div>
          <div
            v-if="routeGame?.gems?.url"
            class="pl-1 pr-6"
          >
            <div
              class="rounded-lg flex justify-start items-center shrink-0 relative pl-5 pr-2 bg-linear-to-b from-[#1d575f] to-[#32788a]"
            >
              <img
                alt="energy"
                :src="routeGame?.gems.url"
                class="absolute"
                :class="[routeGame?.gems?.customClass]"
              >
              <div class="truncate text-white py-0.5 text-lg font-normal tracking-wide">
                {{ gameLoyalty?.gemsHuman || 0 }}
              </div>
            </div>
          </div>
        </div>
        <div class="min-h-6">
          <transition
            name="el-fade-in-linear"
          >
            <div
              v-if="!!gameLoyalty?.purchasesToCollect"
              class="w-full max-w-lg mx-auto text-center pl-2 pr-1 text-white pt-0.5  pb-1 leading-[0.8rem]"
            >
              <AlertSvg class="inline text-[1rem] mr-1" />
              <span class="text-[0.7rem] leading-[0.8rem]">{{ t('loyalty.collectRewards') }}</span>
            </div>
          </transition>
        </div>
      </div>
      <!--  loyalty box -->
      <div class="max-w-lg 2xl:max-w-xl mx-auto flex flex-row text-white">
        <div
          v-for="(tab, indexTab) in currentLeagues"
          :key="`${tab.name}_${indexTab}`"
          class="w-1/5 lyt-tab py-2 uppercase text-center text-[0.7rem] font-black tracking-wider relative transition-all pl-[3px]"
          :class="{
            'active': activeTab === tab.name && activeTab,
            'cursor-pointer group': isLogged,
            'cursor-not-allowed': !isLogged
          }"
          @click="changeTab(tab.name)"
        >
          <el-image
            v-if="indexTab"
            :src="SepUrl"
            alt=""
            class="absolute left-0 top-0 bottom-0"
          />
          <el-image
            v-if="tab?.assetLogo"
            :src="tab.assetLogo"
            :alt="`icon  ${tab.name}`"
            class="big:group-hover:scale-110 transition-all mx-2"
            :class="{ '-translate-x-[3px]': !indexTab }"
          />
          <div class="text-center">
            <span
              :class="{ 'inline-block -translate-x-[3px]': !indexTab, 'text-[#2ef3ff]': activeLeagueData?.name && (tab.name === activeLeagueData?.name) && isLogged }"
            >{{ tab?.label || tLeague(tab.name) }}</span>
          </div>
        </div>
      </div>
      <div class="max-w-xl 2xl:max-w-2xl mx-auto py-4 text-white bg-[#1b5862] overflow-hidden">
        <div class="px-2 text-center my-3">
          <div class="uppercase text-[0.85rem] mini:text-[1rem] font-black tracking-wider leading-[1.5em]">
            <transition
              name="el-fade-in-linear"
              mode="out-in"
            >
              <div
                v-if="isLoggedGoat && !isLogged"
              >
                <span
                  class="underline-offset-4 underline cursor-pointer hover:text-neutral-200 active:text-neutral-100"
                  @click="connectClick"
                >
                  {{ t('loyalty.connectGame') }}
                </span> {{ t('loyalty.joinLoyalty') }}
              </div>
              <div v-else-if="!isLogged">
                <span
                  class="underline-offset-4 underline cursor-pointer hover:text-neutral-200 active:text-neutral-100"
                  @click="loginClick"
                >
                  {{ t('loyalty.logIn') }}
                </span> {{ t('loyalty.joinLoyalty') }}
              </div>
              <div v-else>
                <div>{{ t('loyalty.shopToJoin') }}</div>
                <div class="text-[0.65rem] mini:text[0.9rem] font-light">
                  {{ t('loyalty.leagueRewards') }}
                  <template v-if="(leaguesMap?.[String(activeTab)]?.rewardsCount || 0) === -1">
                    ({{ leaguesMap?.[String(activeTab)]?.receivedRewards || 0 }}/<InfinitySvg class="inline-block text-sm mb-0.5" />)
                  </template>
                  <template v-else-if="currentTabLeague?.name && currentTabLeague.no < (activeLeague?.[routeGame.gameKey]?.no || 0)">
                    ({{ leaguesMap?.[String(activeTab)]?.rewardsCount || 0 }}/{{ leaguesMap?.[String(activeTab)]?.rewardsCount || 0 }})
                  </template>
                  <template v-else>
                    ({{ leaguesMap?.[String(activeTab)]?.receivedRewards || 0 }}/{{ leaguesMap?.[String(activeTab)]?.rewardsCount || 0 }})
                  </template>
                </div>
              </div>
            </transition>
          </div>
        </div>
        <div>
          <transition
            name="el-fade-in-linear"
            mode="out-in"
          >
            <!--  not logged progress bar -->
            <template v-if="!isLogged">
              <div
                v-if="visibleLeagues?.length ?? 0 > 1"
                class="w-full pr-0 min-h-36 mini:min-h-44 lg:min-h-48 mt-6 overflow-x-hidden blur-xs"
              >
                <div class="w-full relative grid grid-cols-[calc(20%-0.5rem)_1fr_calc(20%-1rem)] 2xl:grid-cols-[calc(17%-0.5rem)_1fr_calc(17%-1rem)] items-center">
                  <div
                    class="z-10 relative ml-2 overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      v-if="visibleLeagues?.[0]?.assetLogo"
                      :src="visibleLeagues[0].assetLogo"
                      :alt="`icon  ${visibleLeagues[0].name}`"
                      class="z-10"
                    />
                    <el-image
                      v-if="visibleLeagues?.[0]?.assetLogo"
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                  <div class="h-7 -ml-2 relative">
                    <div class="border-y-4 border-white absolute left-0 top-0 bottom-0 w-[150%] bg-linear-to-b from-[#09262a] to-[#17515a]" />
                    <div class="relative h-full w-full">
                      <div
                        v-if="visibleLeagues?.[0]?.rewards?.length"
                        class="w-full absolute left-0 top-1/2 grid grid-cols-3 gap-4 -translate-y-1"
                      >
                        <template
                          v-for="n in 3"
                          :key="n"
                        >
                          <div
                            v-if="visibleLeagues?.[0]?.rewards?.[n - 1]?.items?.[0]"
                            class="flex flex-col items-center"
                          >
                            <div class="rounded-full bg-black size-2" />
                            <div class="bg-black w-0.5 h-8" />
                            <div class="relative w-full">
                              <div
                                class="bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-[1.6] flex items-center justify-center  w-full max-w-16 sm:max-w-24  mx-auto"
                              >
                                <div class="h-full w-full relative p-1.5">
                                  <div
                                    class="bg-contain bg-no-repeat bg-center h-full w-full"
                                    :style="`background-image: url('${visibleLeagues?.[0]?.rewards?.[n - 1]?.items?.[0]?.asset ?? ''}');`"
                                  />
                                </div>
                              </div>
                              <div class="text-center text-[0.85rem] mini:text-[1rem] gs-text-shadow font-black leading-none -translate-y-1/2">
                                x{{ visibleLeagues?.[0]?.rewards?.[n - 1]?.items?.[0]?.humanAmount ?? '' }}
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="visibleLeagues?.[1]?.assetLogo"
                    class="-translate-x-2 relative overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      :src="visibleLeagues[1].assetLogo"
                      :alt="`icon  ${visibleLeagues[1].name}`"
                      class="z-10"
                    />
                    <el-image
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                </div>
              </div>
            </template>
            <!--  progress logged/connected -->
            <template v-else-if="currentActiveLeagueSelected?.name">
              <div
                v-if="(visibleLeagues?.length ?? 0 > 1) && activeTab"
                class="w-full pr-0 min-h-36 mini:min-h-44 lg:min-h-48"
              >
                <div
                  class="w-full relative grid items-center pt-6"
                  :class="{
                    'grid-cols-[calc(20%-0.5rem)_1fr_calc(20%-1rem)] 2xl:grid-cols-[calc(17%-0.5rem)_1fr_calc(17%-1rem)]': !(allLeagues?.[currentActiveLeagueSelected.index + 1]?.assetLogo && (currentActiveLeagueSelected.rewardsCount - currentActiveLeagueSelected.receivedRewards) > 3),
                    'grid-cols-[calc(20%-0.5rem)_1fr_0.8rem] 2xl:grid-cols-[calc(17%-0.5rem)_1fr_0.8rem]': allLeagues?.[currentActiveLeagueSelected.index + 1]?.assetLogo && (currentActiveLeagueSelected.rewardsCount - currentActiveLeagueSelected.receivedRewards) > 3
                  }"
                >
                  <div
                    v-if="currentActiveLeagueSelected.assetLogo"
                    class="z-10 relative ml-2 overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      :src="currentActiveLeagueSelected.assetLogo"
                      :alt="`icon  ${currentActiveLeagueSelected.name}`"
                      class="z-10"
                    />
                    <el-image
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                  <div class="h-7 -ml-2 relative">
                    <div class="border-y-4 border-white absolute left-0 top-0 bottom-0 w-[150%] bg-linear-to-b from-[#09262a] to-[#17515a]" />
                    <div
                      v-if="currentActiveLeagueSelected.rewardsCount"
                      class="relative h-full w-full"
                    >
                      <div
                        class="absolute top-[4px] bottom-[4px] lef-0 bg-contain bg-repeat-x"
                        :style="`background-image: url('${currentActiveLeagueSelected.assetBar}'); width: ${currentActiveLeagueSelected.progress}`"
                      />
                      <template v-if="currentActiveLeagueSelected.rewards?.length">
                        <div
                          class="w-full absolute left-0 top-1/2 grid -translate-y-1"
                          :class="{
                            'grid-cols-4': (currentActiveLeagueSelected.rewardsCount - currentActiveLeagueSelected.receivedRewards) > 3 || currentActiveLeagueSelected.rewardsCount === -1,
                            'grid-cols-3': (currentActiveLeagueSelected.rewardsCount - currentActiveLeagueSelected.receivedRewards) <= 3
                          }"
                        >
                          <div
                            v-for="(reward, index) in currentActiveLeagueSelected.rewards"
                            :key="reward.id"
                            class="flex flex-col items-center px-1 relative"
                          >
                            <div
                              v-if="currentActiveLeagueSelected.progressLabel && !reward.taken && ((index === 0 && !reward.taken) || (!!index && !!currentActiveLeagueSelected.rewards?.[index - 1]?.taken))"
                              class="text-white gs-text-shadow leading-none font-black absolute top-0 left-0 w-full min-w-min pl-0.5 flex flex-row gap-0.5 items-center justify-center -translate-x-1/2 z-10 bg-[#011c21] py-[2px]"
                              :class="{
                                '-translate-y-12': !!index,
                                '-translate-y-[3.2rem] mini:-translate-y-[4.3rem] xs:-translate-y-18': !index
                              }"
                            >
                              <div class="text-[0.75rem] big:text-[0.9rem]">
                                {{ currentActiveLeagueSelected.progressLabel }}
                              </div>
                              <el-image
                                v-if="routeGame.loyalty.icon"
                                :src="routeGame.loyalty.icon"
                                alt="loyalty icon"
                                class="size-[1.3rem] big:size-6"
                              />
                              <img
                                :src="PointerImgUrl"
                                alt=""
                                class="absolute left-0 size-5 rotate-45 origin-center w-auto translate-y-1/3 -translate-x-[calc(50%+0.5px)] z-[-1]"
                              >
                              <img
                                :src="PointerImgUrl"
                                alt=""
                                class="absolute right-0 size-5 -rotate-[-225deg] origin-center w-auto translate-y-1/3 translate-x-[calc(50%+0.5px)] z-[-1]"
                              >
                            </div>

                            <div class="rounded-full bg-black size-2" />
                            <div class="bg-black w-0.5 h-8" />
                            <div class="relative w-full">
                              <div
                                class="bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-[1.6] flex flex-col items-center justify-center  w-full max-w-16 sm:max-w-24  mx-auto relative"
                              >
                                <div class="absolute top-0.5 pl-0.5  mx-auto  w-full text-left text-[0.6rem] big:text-[0.8rem] gs-text-shadow font-black leading-none">
                                  #{{ reward.no }}
                                </div>
                                <div class="h-full w-full relative p-1.5">
                                  <div
                                    v-if="reward.items?.[0]?.asset"
                                    class="bg-contain bg-no-repeat bg-center h-full w-full"
                                    :style="`background-image: url('${reward.items[0].asset}');`"
                                    :class="{ 'grayscale opacity-50': reward.taken }"
                                  />
                                  <div
                                    v-if="reward.taken"
                                    class="absolute inset-1 flex items-center justify-center bg-contain bg-no-repeat bg-center"
                                    :style="`background-image: url('${CheckImgUrl}');`"
                                  />
                                </div>
                              </div>
                              <div
                                v-if="reward.items?.[0]?.humanAmount"
                                class="text-center text-[0.85rem] mini:text-[1rem] gs-text-shadow font-black leading-none -translate-y-1/2"
                              >
                                x{{ reward.items[0].humanAmount }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div
                    v-if="allLeagues?.[currentActiveLeagueSelected.index + 1]?.assetLogo && (currentActiveLeagueSelected.rewardsCount - currentActiveLeagueSelected.receivedRewards) <= 3"
                    class="-translate-x-2 relative overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      :src="allLeagues?.[currentActiveLeagueSelected.index + 1]?.assetLogo"
                      :alt="`icon  ${allLeagues?.[currentActiveLeagueSelected.index + 1]?.name}`"
                      class="z-10"
                    />
                    <el-image
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                </div>
              </div>
            </template>
            <!-- progress before active leagues -->
            <template v-else-if="currentTabLeague?.name && currentTabLeague.no < (activeLeague?.[routeGame.gameKey]?.no || 0)">
              <div
                v-if="(visibleLeagues?.length ?? 0 > 1) && activeTab"
                class="w-full pr-0 min-h-36 mini:min-h-44 lg:min-h-48 mt-6 overflow-x-hidden"
              >
                <div
                  class="w-full relative grid items-center grid-cols-[calc(20%-0.5rem)_1fr_calc(20%-1rem)]  2xl:grid-cols-[calc(17%-0.5rem)_1fr_calc(17%-1rem)]"
                >
                  <div
                    v-if="currentTabLeague.assetLogo"
                    class="z-10 relative ml-2 overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      :src="currentTabLeague.assetLogo"
                      :alt="`icon  ${currentTabLeague.name}`"
                      class="z-10"
                    />
                    <el-image
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                  <div class="h-7 -ml-2 relative">
                    <div class="border-y-4 border-white absolute left-0 top-0 bottom-0 w-[150%] bg-linear-to-b from-[#09262a] to-[#17515a]" />
                    <div
                      v-if="currentTabLeague.rewardsCount"
                      class="relative h-full w-full"
                    >
                      <div
                        class="absolute top-[4px] bottom-[4px] lef-0 bg-contain bg-repeat-x"
                        :style="`background-image: url('${currentTabLeague.assetBar}'); width: 100%`"
                      />
                      <template v-if="currentTabLeague.rewards?.length">
                        <div
                          class="w-full absolute left-0 top-1/2 grid -translate-y-1 grid-cols-3"
                        >
                          <div
                            v-for="reward in currentTabLeague.rewards.slice(-3)"
                            :key="reward.id"
                            class="flex flex-col items-center px-1"
                          >
                            <div class="rounded-full bg-black size-2" />
                            <div class="bg-black w-0.5 h-8" />
                            <div class="relative w-full">
                              <div
                                class="bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-[1.6] flex flex-col items-center justify-center w-full max-w-16 sm:max-w-24  mx-auto relative"
                              >
                                <div class="absolute top-0.5 w-full text-left text-[0.6rem] big:text-[0.8rem] gs-text-shadow font-black leading-none">
                                  #{{ reward.no }}
                                </div>
                                <div class="h-full w-full relative py-1.5">
                                  <div
                                    v-if="reward.items?.[0]?.asset"
                                    class="bg-contain bg-no-repeat bg-center h-full w-full"
                                    :style="`background-image: url('${reward.items[0].asset}');`"
                                    :class="{ 'grayscale opacity-50': reward.taken }"
                                  />
                                  <div
                                    v-if="reward.taken"
                                    class="absolute inset-1 flex items-center justify-center bg-contain bg-no-repeat bg-center"
                                    :style="`background-image: url('${CheckImgUrl}');`"
                                  />
                                </div>
                              </div>
                              <div
                                v-if="reward.items?.[0]?.humanAmount"
                                class="text-center text-[0.85rem] mini:text-[1rem] gs-text-shadow font-black leading-none -translate-y-1/2"
                              >
                                x{{ reward.items[0].humanAmount }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div
                    v-if="allLeagues?.[currentTabLeague.index + 1]?.assetLogo"
                    class="-translate-x-2 relative overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      :src="allLeagues?.[currentTabLeague.index + 1]?.assetLogo"
                      :alt="`icon  ${allLeagues?.[currentTabLeague.index + 1]?.name}`"
                      class="z-10"
                    />
                    <el-image
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                </div>
              </div>
            </template>
            <!-- not active progress -->
            <template v-else>
              <div
                v-if="(currentTabLeague?.rewardsCount === -1 || (visibleLeagues?.length ?? 0 > 1)) && activeTab"
                class="w-full pr-0 min-h-36 mini:min-h-44 lg:min-h-48 mt-6 overflow-x-hidden"
              >
                <div
                  class="w-full relative grid items-center grid-cols-[calc(20%-0.5rem)_1fr_calc(20%-1rem)]"
                >
                  <div
                    class="z-10 relative ml-2 overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      v-if="leaguesMap?.[activeTab]?.assetLogo"
                      :src="leaguesMap?.[activeTab]?.assetLogo ?? ''"
                      :alt="`icon  ${leaguesMap?.[activeTab]?.name ?? ''}`"
                      class="z-10"
                    />
                    <el-image
                      v-if="leaguesMap?.[activeTab]?.assetLogo"
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                  <div class="h-7 -ml-2 relative">
                    <div class="border-y-4 border-white absolute left-0 top-0 bottom-0 w-[150%] bg-linear-to-b from-[#09262a] to-[#17515a]" />
                    <div
                      v-if="leaguesMap?.[activeTab]?.rewardsCount"
                      class="relative h-full w-full"
                    >
                      <template v-if="currentTabLeague?.rewardsCount === -1">
                        <div
                          class="w-full absolute left-0 top-1/2 grid grid-cols-1 gap-3 -translate-y-1"
                        >
                          <div
                            class="flex flex-col items-center"
                          >
                            <div class="rounded-full bg-black size-2" />
                            <div class="bg-black w-0.5 h-8" />
                            <div class="relative w-full">
                              <div
                                class="bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-[1.3]  p-1 flex items-center justify-center mx-auto max-w-14 sm:max-w-16 big:max-w-20"
                              >
                                <div
                                  class="bg-contain bg-no-repeat bg-center h-full w-full"
                                  :style="`background-image: url('${GiftUrl}');`"
                                />
                              </div>
                              <div
                                class="text-center text-[0.85rem] mini:text-[1rem] gs-text-shadow font-black leading-none -translate-y-1/2"
                              >
                                <InfinitySvg class="inline-block text-[1.5rem] sm:text-3xl stroke-24" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="(leaguesMap?.[activeTab]?.rewardsCount || 0) > 5">
                        <div
                          class="w-full absolute left-0 top-1/2 grid grid-cols-5 gap-3 -translate-y-1"
                        >
                          <div
                            v-for="n in [
                              { type: 'number', val: 0 },
                              { type: 'number', val: 1 },
                              { type: 'dots', val: -1 },
                              { type: 'number', val: (leaguesMap?.[activeTab]?.rewardsCount || 0) - 1 },
                              { type: 'number', val: leaguesMap?.[activeTab]?.rewardsCount || 0 }
                            ]"
                            :key="n.val"
                            class="flex flex-col items-center"
                          >
                            <div class="rounded-full bg-black size-2" />
                            <div class="bg-black w-0.5 h-8" />
                            <div class="relative w-full">
                              <div
                                class="bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-square  p-1 flex items-center justify-center mx-auto max-w-10 sm:max-w-14"
                              >
                                <div
                                  v-if="n.type === 'number'"
                                  class="bg-contain bg-no-repeat bg-center h-full w-full"
                                  :style="`background-image: url('${GiftUrl}');`"
                                />
                                <Dots
                                  v-else-if="n.type === 'dots'"
                                  class="text-4xl"
                                />
                              </div>
                              <div
                                v-if="n.type === 'number'"
                                class="text-center text-[0.85rem] mini:text-[1rem] gs-text-shadow font-black leading-none -translate-y-1/2"
                              >
                                {{ n.val }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div
                          class="w-full absolute left-0 top-1/2 grid grid-cols-5 gap-3 -translate-y-1"
                        >
                          <div
                            v-for="(n, nIndex) in [
                              { type: 'number', val: (leaguesMap?.[activeTab]?.rewardsCount || 0) >= 1 ? 0 : -1 },
                              { type: 'number', val: (leaguesMap?.[activeTab]?.rewardsCount || 0) >= 2 ? 1 : -1 },
                              { type: 'number', val: (leaguesMap?.[activeTab]?.rewardsCount || 0) >= 3 ? 2 : -1 },
                              { type: 'number', val: (leaguesMap?.[activeTab]?.rewardsCount || 0) >= 4 ? 3 : -1 },
                              { type: 'number', val: (leaguesMap?.[activeTab]?.rewardsCount || 0) >= 5 ? 4 : -1 }
                            ]"
                            :key="nIndex"
                            class="flex flex-col items-center"
                          >
                            <template v-if="n.val > -1">
                              <div class="rounded-full bg-black size-2" />
                              <div class="bg-black w-0.5 h-8" />
                              <div class="relative w-full">
                                <div
                                  class="bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-square  p-1 flex items-center justify-center mx-auto max-w-10 sm:max-w-14"
                                >
                                  <div
                                    class="bg-contain bg-no-repeat bg-center h-full w-full"
                                    :style="`background-image: url('${GiftUrl}');`"
                                  />
                                </div>
                                <div
                                  class="text-center text-[0.85rem] mini:text-[1rem] gs-text-shadow font-black leading-none -translate-y-1/2"
                                >
                                  {{ n.val }}
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div
                    v-if="leaguesMap?.[activeTab]?.index !== undefined && allLeagues?.[(leaguesMap?.[activeTab]?.index || 0) + 1]?.assetLogo"
                    class="-translate-x-2 relative overflow-y-visible flex flex-col items-center justify-center"
                  >
                    <el-image
                      :src="allLeagues?.[(leaguesMap?.[activeTab]?.index || 0) + 1]?.assetLogo"
                      :alt="`icon  ${allLeagues?.[(leaguesMap?.[activeTab]?.index || 0) + 1]?.name}`"
                      class="z-10"
                    />
                    <el-image
                      :src="RibbonUrl"
                      alt=""
                      class="absolute left-0 -bottom-[0.7rem] big:-bottom-[1.2rem] w-1/2 translate-x-1/2 z-0"
                    />
                  </div>
                </div>
              </div>
            </template>
          </transition>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="postcss">
.lyt-tab {
  background-color: #0a343b;

  &.active {
    background-color: #1b5862;
  }
}
</style>
